import { computed, Injectable, signal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { EventAccessApiService } from './event-access-api.service';
import { SmEventApiService } from '../sales-manager/sm-event-api.service';
import { EventApiService } from '../event/event-api.service';
import { SelectionStateService } from '../selection-state.service';
import { UserProfileServiceService } from '../profile/user-profile-service.service';
import { EventAccessItem, EventListTab, EventRole } from 'src/app/types/event-access.types';
import { EventListOrder, mergeEventSources } from 'src/app/utils/event-access/merge-event-sources';
import { latestRequestTracker, LatestRequestTracker } from 'src/app/utils/reports/latest-request';
import {
  EventSourceDeps,
  EventSourceKey,
  EventSourceResult,
  fetchSource,
  sourceKeysFor,
} from './event-access-sources';

const CACHE_DURATION = 5 * 60 * 1000;
const TABS: readonly EventListTab[] = ['active', 'ended', 'inactive'];

export interface EventTabState {
  readonly items: readonly EventAccessItem[];
  readonly page: number;
  /** Fuentes que todavía tienen páginas por pedir. */
  readonly moreBySource: Readonly<Partial<Record<EventSourceKey, boolean>>>;
  readonly hasMore: boolean;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  /** Alguna fuente falló: la lista puede estar incompleta (aviso no bloqueante). */
  readonly partialFailure: boolean;
  readonly fetchedAt: number | null;
  readonly query: string;
  readonly userId: number | null;
}

export interface LoadOptions {
  readonly force?: boolean;
  readonly q?: string;
}

const EMPTY_TAB: EventTabState = {
  items: [],
  page: 0,
  moreBySource: {},
  hasMore: false,
  loading: false,
  loadingMore: false,
  partialFailure: false,
  fetchedAt: null,
  query: '',
  userId: null,
};

type TabsState = Readonly<Record<EventListTab, EventTabState>>;

const INITIAL_TABS: TabsState = { active: EMPTY_TAB, ended: EMPTY_TAB, inactive: EMPTY_TAB };

/** Une las páginas de las fuentes sobre lo ya cargado. */
const ORDER_BY_TAB: Readonly<Record<EventListTab, EventListOrder>> = { active: 'upcoming', ended: 'past', inactive: 'past' };

function applyResults(
  tab: EventListTab,
  results: readonly EventSourceResult[],
  base: EventTabState,
): Partial<EventTabState> {
  const moreBySource = results.reduce(
    (acc, result) => ({ ...acc, [result.key]: result.hasMore }),
    base.moreBySource,
  );
  return {
    items: mergeEventSources(results.flatMap(result => result.entries), base.items, ORDER_BY_TAB[tab]),
    moreBySource,
    hasMore: Object.values(moreBySource).some(Boolean),
    partialFailure: base.partialFailure || results.some(result => result.failed),
  };
}

/**
 * Eventos del usuario `sellers` con sus roles de entrada, por pestaña. Une my-events con las
 * fuentes de jefe/admin, cachea 5 min por usuario y tolera la caída de una fuente.
 */
@Injectable({
  providedIn: 'root'
})
export class EventAccessService {
  private readonly _tabs = signal<TabsState>(INITIAL_TABS);
  private readonly _activeTab = signal<EventListTab>('active');
  private readonly _trackers: Readonly<Record<EventListTab, LatestRequestTracker>> = {
    active: latestRequestTracker(),
    ended: latestRequestTracker(),
    inactive: latestRequestTracker(),
  };
  private readonly _deps: EventSourceDeps;
  private readonly _userId = computed(() => this._profile.userProfile()?.id ?? null);

  public readonly activeTab = this._activeTab.asReadonly();
  /** Estado de la pestaña activa (vacío si la caché es de otro usuario). */
  public readonly state = computed(() => this.visibleState(this._activeTab()));
  public readonly events = computed(() => this.state().items);

  constructor(
    accessApi: EventAccessApiService,
    smApi: SmEventApiService,
    eventApi: EventApiService,
    logger: NGXLogger,
    private _selectionState: SelectionStateService,
    private _profile: UserProfileServiceService,
  ) {
    this._deps = { accessApi, smApi, eventApi, logger };
  }

  public async load(tab: EventListTab, { force = false, q }: LoadOptions = {}): Promise<void> {
    this._activeTab.set(tab);
    await this.fetchFirstPage(tab, force, q);
  }

  /** Siguiente página de la pestaña activa, solo a las fuentes que tienen más. */
  public async loadMore(): Promise<void> {
    const tab = this._activeTab();
    const current = this.visibleState(tab);
    if (!current.hasMore || current.loading || current.loadingMore) {
      return;
    }
    const keys = (Object.keys(current.moreBySource) as EventSourceKey[]).filter(key => current.moreBySource[key]);
    const page = current.page + 1;
    const isLatest = this._trackers[tab].start();
    this.patch(tab, { loadingMore: true });

    const results = await firstValueFrom(this.fetchAll(keys, tab, page, current.query));
    if (!isLatest()) {
      return;
    }
    this.patch(tab, { ...applyResults(tab, results, current), page, loadingMore: false });
  }

  /** Marca la caché como vencida (pull-to-refresh); lo cargado se sigue viendo hasta recargar. */
  public invalidate(): void {
    this._tabs.update(tabs => TABS.reduce((acc, tab) => ({ ...acc, [tab]: { ...tabs[tab], fetchedAt: null } }), tabs));
  }

  /** Logout / login: no queda nada del usuario anterior y se descartan respuestas en vuelo. */
  public reset(): void {
    TABS.forEach(tab => this._trackers[tab].start());
    this._tabs.set(INITIAL_TABS);
    this._activeTab.set('active');
  }

  /** Roles del evento según lo cacheado en cualquier pestaña, sin requests. */
  public rolesFor(eventId: number): readonly EventRole[] {
    const item = TABS.map(tab => this.visibleState(tab))
      .flatMap(state => state.items)
      .find(entry => entry.event.id === eventId);
    return item?.roles ?? [];
  }

  /** Para deep links: si el evento no está cacheado, carga la primera página de Vigentes. */
  public async ensureRolesFor(eventId: number): Promise<readonly EventRole[]> {
    const cached = this.rolesFor(eventId);
    if (cached.length) {
      return cached;
    }
    await this.fetchFirstPage('active', false);
    return this.rolesFor(eventId);
  }

  private async fetchFirstPage(tab: EventListTab, force: boolean, q?: string): Promise<void> {
    const current = this.visibleState(tab);
    const query = (q ?? current.query).trim();
    if (!force && this.isFresh(current, query)) {
      return;
    }
    const isLatest = this._trackers[tab].start();
    const userId = this._userId();
    this.patch(tab, { ...current, loading: true, loadingMore: false, query, userId });

    const keys = sourceKeysFor(tab, {
      isBusinessAdmin: this._selectionState.isBusinessAdmin(),
      isBoss: this._selectionState.isBoss(),
    });
    const results = await firstValueFrom(this.fetchAll(keys, tab, 1, query));
    if (!isLatest()) {
      return;
    }
    this.patch(tab, {
      ...applyResults(tab, results, { ...EMPTY_TAB, query, userId }),
      page: 1,
      loading: false,
      fetchedAt: Date.now(),
    });
  }

  private fetchAll(keys: readonly EventSourceKey[], tab: EventListTab, page: number, q: string): Observable<EventSourceResult[]> {
    if (!keys.length) {
      return of([]);
    }
    return forkJoin(keys.map(key => fetchSource(key, { tab, page, q }, this._deps)));
  }

  /** Una carga con fuentes caídas (p. ej. sin Jefe/Admin) no cuenta como fresca: se reintenta. */
  private isFresh(state: EventTabState, query: string): boolean {
    return state.fetchedAt !== null
      && !state.partialFailure
      && state.query === query
      && Date.now() - state.fetchedAt < CACHE_DURATION;
  }

  /** La caché de otro usuario no se muestra ni se reusa. */
  private visibleState(tab: EventListTab): EventTabState {
    const state = this._tabs()[tab];
    return state.userId === this._userId() ? state : EMPTY_TAB;
  }

  private patch(tab: EventListTab, changes: Partial<EventTabState>): void {
    this._tabs.update(tabs => ({ ...tabs, [tab]: { ...tabs[tab], ...changes } }));
  }
}
